/**
 * THE WORDS OF EVERY CLIENT EMAIL OPS COMPOSES (ops #76, O6 and O9; `docs/plan-o6-o9-emails.md`
 * section 2), approved by the owner word for word on 2026-10-19. Ops composes and home delivers, from
 * home@ (CLAUDE.md, the seams' rule 3), so every word a client reads in an email from ops is here:
 * each email a pure function of the record's own values, filled at composing, and never a sentence
 * any AI wrote. The office's own words (E8) are the one exception, and they are the office's, under
 * the claims check at the door they are typed at, with a fixed subject and the footer added here.
 *
 * ONE SHAPE. Plain text, no HTML part. Every email opens "Hi Karen, this is Faithfully Kept." with her
 * first name, "Hi, this is Faithfully Kept." without one; a blank line between paragraphs; her link,
 * where there is one, on a line of its own with nothing after it; and the two lines of `FOOTER` last.
 * Replies go to hello@, so an email says "reply to this email, or call", and never asks her to text.
 *
 * THE FIGURES ARE THE RECORD'S AND NOTHING ELSE. A day arrives as `YYYY-MM-DD` and is written as
 * `longDay` writes it ("Thursday, October 29"), money arrives in whole cents and is written by
 * `formatCents` ("$212.00"), an invoice and a receipt number as ops gives them ("FK-42", "FK-R7"). The
 * arrival window, the business hours and her saved method arrive already in the words their own
 * writers give them (`windowText`, `businessHoursWords`, `savedMethodWords`). A home is named by the
 * office's label only where the caller passes one, which it does only where the household has more
 * than one live home. Never a price beyond the record's own figures, never a street, never a
 * cleaner's name, never a link but hers, never a credential. Each email names the figures it was
 * filled with (`amountsCents`), so the claims check at composing allows those by value and no other
 * (`api/src/seams/email-words.ts`).
 *
 * SPELLED ONCE. The fixed lines are here, and the business number is `BUSINESS_PHONE_DISPLAY`, never
 * typed in this file. Two approved strings are spelled where a test holds them to one file: how to
 * stop, the ending of every autopay text (`HOW_TO_STOP`, `autopay.ts`), and the Zelle tag, decoded from
 * the one stored code (`ZELLE`).
 *
 * VERSIONED BY THEIR WORDS. `EMAIL_WORDS_VERSION` is the hex SHA-256 of `emailWordsCorpus()`: the fixed
 * lines and every email in every form section 2 prints, filled with its example, Karen, invoice FK-42
 * of $212.00 for the clean of Thursday, October 29. It is pinned rather than computed at load, since a
 * digest is async, and `api/test/emails.test.ts` computes it again and holds every form to the owner's
 * text, as `AUTOPAY_TERMS_VERSION` is held: a changed word is a changed version the owner approves
 * again.
 */
import { HOW_TO_STOP } from './autopay.ts';
import { BUSINESS_EMAIL, BUSINESS_SITE } from './documents.ts';
import { BUSINESS_PHONE_DISPLAY, type PaymentMethod } from './enums.ts';
import { formatCents } from './rate-card.ts';
import { ZELLE } from './zelle.ts';

// ---------------------------------------------------------------------------------------------
// The fixed lines
// ---------------------------------------------------------------------------------------------

/** How to reach a person, in every email: a reply goes to hello@, and the number answers. */
export const QUESTIONS = `Questions? Reply to this email, or call ${BUSINESS_PHONE_DISPLAY}.`;

/**
 * Every money email, the sentence the PDFs already print (approved there first), spelled here once:
 * the invoice and the receipt print this constant (`DOCUMENT_WORDS.common.antiFraud`).
 */
export const NEVER_CHANGE = `We never change how you pay us by email or text. If a message asks you to pay anyone else, call ${BUSINESS_PHONE_DISPLAY} first.`;

/** On the invoice and the receipt, only for a person whose membership at home is `granted`. */
export const DOOR_LINE = 'It is also in Payments on your home page at home.faithfullykept.com.';

/** The last two lines of every email, the office's own words included. */
export const FOOTER = `Faithfully Kept\n${BUSINESS_PHONE_DISPLAY}, ${BUSINESS_EMAIL}, ${BUSINESS_SITE}`;

/** Where no live link to her invoice exists: a system-composed email cannot mint one. */
export const NO_LINK =
  'If you no longer have the email with your invoice link, reply to this email and we will send a fresh one.';

/**
 * AFTER HER LINK TO A PAGE AT HOME, its own paragraph, in every email that carries one, on a host whose
 * clients reach home (`CLIENT_PAGES_AT_HOME`): the owner's first sentence of question (d), approved
 * word for word on 2026-10-19 (`docs/plan-clients-only-home.md` section 8, D13). With no invitation
 * email of home's own, the invoice email is the first she hears of home's sign-in by a code.
 */
export const SIGN_IN_LINE =
  'If the page asks you to sign in, enter the email address this was sent to and we will email you a code.';

/**
 * IN PLACE OF E1'S LINK for a payer who holds no `client` door at home, on such a host: the owner's
 * second sentence of question (d), approved word for word on 2026-10-19 (D4), so E1 still asks "How
 * did we do?" before it names Zelle, as the owner ruled, and a reply reaches the office.
 */
export const ANSWER_BY_REPLY = 'How did we do? Reply to this email and tell us.';

/**
 * The hex SHA-256 of `emailWordsCorpus()`, as UTF-8. Pinned, and held to the words by a test. Re-pinned
 * on 2026-10-19 for the two forms ops settled that day within the owner's rules and posted on ops #76:
 * E3 across several invoices, and E5's needs-authentication form without the saved-method sentence.
 * Re-pinned again the same day for the owner's approval of 2026-10-19, at about 23:50 UTC, of the two
 * sentences of question (d) (`SIGN_IN_LINE`, `ANSWER_BY_REPLY`), with the forms of E1 and E1r on a
 * host whose clients reach home that carry them, the door line left out there (D1) and E1's no-door
 * form (D4) (`docs/plan-clients-only-home.md` concern B1a). Re-pinned once more under that same approval
 * of the owner's, 2026-10-19, for the forms of E2, E5 and E6 on such a host (concern B1b): the first
 * sentence (`SIGN_IN_LINE`) after her invoice page at home, E2's door line left out (D1), and each
 * one's no-door form (D4), which leaves out the sentence and clause naming an invoice page with
 * `NO_LINK` and adds no words.
 */
export const EMAIL_WORDS_VERSION =
  'e65b0cf718f6c5ced52e5f1dad706a71aed4fc72faa302e251804cbc1dd41b2f';

// ---------------------------------------------------------------------------------------------
// The shape every email shares
// ---------------------------------------------------------------------------------------------

/** One email's words, as the push carries them, and the figures the record filled them with. */
export interface EmailWords {
  readonly subject: string;
  readonly text: string;
  /** Every figure the words name, in cents, so the claims check allows those by value and no other. */
  readonly amountsCents: readonly number[];
}

/** An invoice the money touched, and what is still owed on it after. */
export interface InvoiceOwed {
  readonly number: string;
  readonly stillOwedCents: number;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** A day as the record keeps it. */
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "Thursday, October 29", as `longDay` in `api/src/schedule/messages.ts` writes it, which the test
 * holds this to for every day of two years. A day that is not one throws: an email is never written
 * with a day it could not read.
 */
function dayWords(day: string): string {
  const match = ISO_DAY.exec(day);
  const [year, month, date] = match ? [Number(match[1]), Number(match[2]), Number(match[3])] : [];
  const at = year === undefined ? null : new Date(Date.UTC(year, month! - 1, date));
  if (at === null || at.getUTCMonth() !== month! - 1 || at.getUTCDate() !== date) {
    throw new Error(`not a day: ${day}`);
  }
  return `${WEEKDAYS[at.getUTCDay()]}, ${MONTHS[month! - 1]} ${date}`;
}

/** Words the office typed, trimmed, or null where there are none. */
const typed = (words: string | null): string | null => words?.trim() || null;

/** "Hi Karen, this is Faithfully Kept.", or "Hi, this is Faithfully Kept." with no name on file. */
export function greetingFor(firstName: string | null): string {
  const name = typed(firstName);
  return name ? `Hi ${name}, this is Faithfully Kept.` : 'Hi, this is Faithfully Kept.';
}

/** The greeting, the paragraphs that are there, and the footer, a blank line between each. */
function emailText(firstName: string | null, paragraphs: readonly (string | false)[]): string {
  const there = paragraphs.filter((paragraph): paragraph is string => paragraph !== false);
  return [greetingFor(firstName), ...there, FOOTER].join('\n\n');
}

/** "A", "A and B", "A, B and C". */
function listed(items: readonly string[]): string {
  return items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The figures an email names, each once. */
const figures = (...cents: number[]): readonly number[] => [...new Set(cents)];

// ---------------------------------------------------------------------------------------------
// E1. The invoice (`invoice_sent`, at "Send the invoice")
// ---------------------------------------------------------------------------------------------

export interface InvoiceEmail {
  readonly firstName: string | null;
  /** The invoice's number, `FK-42`, which is also its Zelle memo once it has one. */
  readonly number: string;
  readonly totalCents: number;
  readonly dueOn: string;
  /** The day of the clean it bills, or null for an invoice that bills no visit. */
  readonly visitDay: string | null;
  /** The home's label, passed only where the household has more than one live home. */
  readonly homeLabel: string | null;
  /**
   * Her link to the invoice: where `atHome` is not set, as on both hosts today, the link minted in the
   * batch that composes the email; where it is, her invoice page at home, or null for a payer who holds
   * no `client` door there, whose email takes its no-door form (D4).
   */
  readonly link: string | null;
  /** The number of the invoice this one replaces, which is cancelled, or null. */
  readonly replaces: string | null;
  /**
   * Whether her membership at home is `granted` as a `client`'s, so the door line is true for her: a
   * `member` is shown no money there. Never said where `atHome` is set (D1).
   */
  readonly door: boolean;
  /**
   * WHETHER THE HOST SENDS HER TO HOME'S PAGES (`CLIENT_PAGES_AT_HOME`, `docs/plan-clients-only-home.md`
   * concern B1a): the door line leaves, since "also" is false once the link is home's (D1); her link is
   * followed by `SIGN_IN_LINE` (D13); and with no link the email takes its no-door form, `ANSWER_BY_REPLY`
   * in place of the link and no invoice page named (D4). Absent or false, every word is as it was.
   */
  readonly atHome?: boolean;
  /** Where her saved method pays it: its words, the business hours, and whether a receipt is emailed. */
  readonly savedMethod: {
    readonly words: string;
    readonly businessHours: string;
    readonly receiptByEmail: boolean;
  } | null;
}

/**
 * E1. It asks "How did we do?" before it names Zelle, the owner's rule, and where her saved method
 * pays the invoice the Zelle paragraph is the sentence the office already sends, with one more.
 *
 * At home (`atHome`), her link is followed by the sign-in sentence and the door line leaves; with no
 * link, for a payer who holds no `client` door there, she has no invoice page, so the question is
 * asked by `ANSWER_BY_REPLY` and every "on your invoice page" is left out, Zelle and her saved method
 * kept (`docs/plan-clients-only-home.md` section 3, D4).
 */
export function invoiceEmail(input: InvoiceEmail): EmailWords {
  const total = formatCents(input.totalCents);
  const due = dayWords(input.dueOn);
  const clean = input.visitDay === null ? null : dayWords(input.visitDay);
  const label = typed(input.homeLabel);
  const atHome = input.atHome === true;
  const link = input.link;
  const opening =
    clean === null
      ? `Here is invoice ${input.number}: ${total}, due ${due}.`
      : `Here is invoice ${input.number} for your clean${label ? ` at the ${label}` : ''} on ${clean}: ${total}, due ${due}.`;
  const saved = input.savedMethod;
  const howToPay =
    saved === null
      ? `The easiest way to pay is Zelle to ${ZELLE.tag}, memo ${input.number}.${link === null ? '' : ' Every way to pay is on your invoice page.'}`
      : `Once you tell us the clean went well, we will charge your ${saved.words} during business hours: ${saved.businessHours}.${saved.receiptByEmail ? ' A receipt is emailed when it is charged.' : ''}${link === null ? '' : ' To pay this one by Zelle instead, press "I\'m paying by Zelle" on your invoice page.'}`;
  return {
    subject: `Your Faithfully Kept invoice ${input.number}: ${total}${clean === null ? '' : ` for your clean on ${clean}`}`,
    text: emailText(input.firstName, [
      opening,
      input.replaces !== null && `It replaces invoice ${input.replaces}, which is cancelled.`,
      link === null
        ? ANSWER_BY_REPLY
        : `How did we do? Tap a face on your invoice page, where you can also see the invoice, download it as a PDF and pay it:\n${link}`,
      link !== null && atHome && SIGN_IN_LINE,
      howToPay,
      NEVER_CHANGE,
      input.door && !atHome && DOOR_LINE,
      QUESTIONS,
    ]),
    amountsCents: figures(input.totalCents),
  };
}

// ---------------------------------------------------------------------------------------------
// E1r. The reminder of the question (`invoice_sent`, the ladder, by email)
// ---------------------------------------------------------------------------------------------

export interface SurveyReminderEmail {
  readonly firstName: string | null;
  /** The day of the clean, or null where the invoice names none the ladder could read. */
  readonly visitDay: string | null;
  /**
   * Her live link, found in the words that asked; where `atHome` is set, the clean's feedback page at
   * home, or her invoice page there where the ladder read no clean (D9).
   */
  readonly link: string;
  /**
   * Whether the host sends her to home's pages (`CLIENT_PAGES_AT_HOME`), so `SIGN_IN_LINE` follows her
   * link (D13). Absent or false, every word is as it was.
   */
  readonly atHome?: boolean;
}

/**
 * THE QUESTION, before her link: a service question about one clean, never a request for a review,
 * with D17's sentence bounded as D17 bounds the promise, "before your next clean", never the
 * open-ended "tell us". `surveyReminderWords` (`api/src/money/pay-link.ts`) reads it too.
 */
export function surveyReminderQuestion(visitDay: string | null): string {
  const clean =
    visitDay === null ? 'your last clean' : `the clean at your home on ${dayWords(visitDay)}`;
  return `Did ${clean} go well? We would like to know either way. If anything was not right, tell us before your next clean and we will clean it again at no charge. Tap the link to tell us, or reply here:`;
}

/** E1r. There is no text form: the reminder of the question goes by email only (phone#27). */
export function surveyReminderEmail(input: SurveyReminderEmail): EmailWords {
  return {
    subject:
      input.visitDay === null
        ? 'How did your last clean go?'
        : `How did your clean on ${dayWords(input.visitDay)} go?`,
    text: emailText(input.firstName, [
      `${surveyReminderQuestion(input.visitDay)}\n${input.link}`,
      input.atHome === true && SIGN_IN_LINE,
      QUESTIONS,
    ]),
    amountsCents: [],
  };
}

// ---------------------------------------------------------------------------------------------
// E2. The receipt (`payment_receipt`)
// ---------------------------------------------------------------------------------------------

export interface ReceiptEmail {
  readonly firstName: string | null;
  /** The receipt's number, `FK-R7`. */
  readonly receiptNumber: string;
  readonly amountCents: number;
  readonly receivedOn: string;
  /**
   * Her live link to the first invoice the money settled, or null for the no-link form; where `atHome`
   * is set, her invoice page at home, or null for a person who holds no `client` door there, whose
   * receipt takes its no-door form (D4).
   */
  readonly link: string | null;
  /** Whether her door at home is `granted` as a `client`'s, so the door line is true. Never said at home. */
  readonly door: boolean;
  /**
   * WHETHER THE HOST SENDS HER TO HOME'S PAGES (`CLIENT_PAGES_AT_HOME`, `docs/plan-clients-only-home.md`
   * concern B1b): her link is followed by `SIGN_IN_LINE` (D13) and the door line leaves (D1); with no
   * link the sentence naming her invoice page leaves with `NO_LINK` (D4). Absent or false, every word is
   * as it was.
   */
  readonly atHome?: boolean;
  /** She paid, however it came; or her saved method was charged. */
  readonly paid:
    | {
        readonly how: 'paid';
        readonly method: PaymentMethod;
        /** The invoices it settled, in order, each with what is still owed on it. */
        readonly invoices: readonly InvoiceOwed[];
        readonly tipCents: number;
        /** The money not yet applied to an invoice, which stays on her account. */
        readonly leftOverCents: number;
      }
    | {
        readonly how: 'charged';
        /** Her saved method as `savedMethodWords` writes it: "Visa ending 4242". */
        readonly savedMethod: string;
        readonly invoiceNumber: string;
        /** The office's release opened the gate, rather than her own word that the clean went well. */
        readonly afterOfficeCheck: boolean;
      };
}

/** How she paid, as the receipt says it; `other` is the sentence without a method. */
const RECEIVED_HOW: Readonly<Record<PaymentMethod, string | null>> = Object.freeze({
  card: 'by card',
  bank_transfer: 'from your bank account',
  zelle: 'by Zelle',
  check: 'by check',
  cash: 'in cash',
  other: null,
});

/** "invoice FK-41, which is now paid in full, and invoice FK-42, on which $50.00 is still owed". */
function settled(invoices: readonly InvoiceOwed[]): string {
  const each = invoices.map((invoice) =>
    invoice.stillOwedCents > 0
      ? `invoice ${invoice.number}, on which ${formatCents(invoice.stillOwedCents)} is still owed`
      : `invoice ${invoice.number}, which is now paid in full`,
  );
  return each.length < 2
    ? (each[0] ?? '')
    : `${each.slice(0, -1).join(', ')}, and ${each[each.length - 1]}`;
}

/**
 * E2. One receipt a payment, however the money came. The automatic charge's form says it was
 * automatic and how to stop.
 *
 * At home (`atHome`), her link is followed by the sign-in sentence and the door line leaves; with no
 * link, for a person who holds no `client` door there, she has no invoice page, so the sentence naming
 * it leaves with `NO_LINK`, and nothing is said in its place (`docs/plan-clients-only-home.md` D4).
 */
export function receiptEmail(input: ReceiptEmail): EmailWords {
  const amount = formatCents(input.amountCents);
  const day = dayWords(input.receivedOn);
  const atHome = input.atHome === true;
  const link = input.link;
  const paid = input.paid;
  let first: string[];
  let amounts: readonly number[];
  if (paid.how === 'charged') {
    const gate = paid.afterOfficeCheck
      ? 'after we checked with you that the clean went well'
      : 'after you told us the clean went well';
    first = [
      `We charged ${amount} to your ${paid.savedMethod} on ${day}, for invoice ${paid.invoiceNumber}, ${gate}, and it is now paid in full.`,
      HOW_TO_STOP,
    ];
    amounts = figures(input.amountCents);
  } else {
    const how = RECEIVED_HOW[paid.method];
    const sentences = [
      `Thank you. We received ${amount}${how ? ` ${how}` : ''} on ${day}${paid.invoices.length > 0 ? `, for ${settled(paid.invoices)}` : ''}.`,
    ];
    if (paid.tipCents > 0) {
      sentences.push(
        `That includes a tip of ${formatCents(paid.tipCents)} for your cleaner, thank you.`,
      );
    }
    if (paid.leftOverCents > 0) {
      sentences.push(
        `${formatCents(paid.leftOverCents)} of it is not yet applied to an invoice and stays on your account.`,
      );
    }
    first = [sentences.join(' ')];
    amounts = figures(
      input.amountCents,
      ...paid.invoices.map((invoice) => invoice.stillOwedCents).filter((cents) => cents > 0),
      ...[paid.tipCents, paid.leftOverCents].filter((cents) => cents > 0),
    );
  }
  return {
    subject: `Your Faithfully Kept receipt ${input.receiptNumber}: ${amount} received, thank you`,
    text: emailText(input.firstName, [
      ...first,
      link === null
        ? !atHome && `Your receipt ${input.receiptNumber} is on your invoice page. ${NO_LINK}`
        : `Your receipt ${input.receiptNumber} is on your invoice page, where you can download it as a PDF:\n${link}`,
      link !== null && atHome && SIGN_IN_LINE,
      NEVER_CHANGE,
      input.door && !atHome && DOOR_LINE,
      QUESTIONS,
    ]),
    amountsCents: amounts,
  };
}

// ---------------------------------------------------------------------------------------------
// E3. A refund (`refund_notice`)
// ---------------------------------------------------------------------------------------------

export interface RefundEmail {
  readonly firstName: string | null;
  readonly amountCents: number;
  readonly refundedOn: string;
  readonly method: PaymentMethod;
  /** The invoices the refund was applied to, or none for a refund from the money on her account. */
  readonly invoices: readonly InvoiceOwed[];
}

/** How the money went back, as the notice says it; `other` is the sentence without a method. */
const REFUNDED_HOW: Readonly<Record<PaymentMethod, string | null>> = Object.freeze({
  card: 'to your card',
  bank_transfer: 'to your bank account',
  zelle: 'by Zelle',
  check: 'by check',
  cash: 'in cash',
  other: null,
});

/**
 * E3. A refund to a card says how long a card takes. A refund Stripe hands back across several
 * invoices names each together, with one owed sentence an invoice: a form made of section 2's
 * sentences alone, which section 2 does not print, approved by ops on 2026-10-19 within the owner's
 * rules and posted on ops #76, and counted in `EMAIL_WORDS_VERSION` with the rest.
 */
export function refundEmail(input: RefundEmail): EmailWords {
  const amount = formatCents(input.amountCents);
  const how = REFUNDED_HOW[input.method];
  const whence =
    input.invoices.length === 0
      ? 'from the money on your account'
      : `for ${listed(input.invoices.map((invoice) => `invoice ${invoice.number}`))}`;
  const sentences = [
    `We refunded ${amount}${how ? ` ${how}` : ''} on ${dayWords(input.refundedOn)}, ${whence}.`,
  ];
  if (input.method === 'card') {
    sentences.push('A refund to a card can take 5 to 10 days to show on your statement.');
  }
  for (const invoice of input.invoices) {
    sentences.push(
      invoice.stillOwedCents > 0
        ? `${formatCents(invoice.stillOwedCents)} is still owed on invoice ${invoice.number}.`
        : `Nothing is owed on invoice ${invoice.number}.`,
    );
  }
  return {
    subject: `Your refund of ${amount} from Faithfully Kept`,
    text: emailText(input.firstName, [sentences.join(' '), NEVER_CHANGE, QUESTIONS]),
    amountsCents: figures(
      input.amountCents,
      ...input.invoices.map((invoice) => invoice.stillOwedCents).filter((cents) => cents > 0),
    ),
  };
}

// ---------------------------------------------------------------------------------------------
// E4. A cancelled invoice (`invoice_cancelled`)
// ---------------------------------------------------------------------------------------------

export interface CancelledInvoiceEmail {
  readonly firstName: string | null;
  readonly number: string;
  readonly visitDay: string | null;
}

/** E4. No paid form: the void refuses an invoice with any live allocation on it. */
export function cancelledInvoiceEmail(input: CancelledInvoiceEmail): EmailWords {
  const clean = input.visitDay === null ? '' : ` for your clean on ${dayWords(input.visitDay)},`;
  return {
    subject: `Invoice ${input.number} from Faithfully Kept is cancelled`,
    text: emailText(input.firstName, [
      `We have cancelled invoice ${input.number},${clean} so nothing is owed on it. If a new invoice replaces it, it will come in its own email with its own number.`,
      NEVER_CHANGE,
      QUESTIONS,
    ]),
    amountsCents: [],
  };
}

// ---------------------------------------------------------------------------------------------
// E5. A payment that did not go through (`payment_problem`)
// ---------------------------------------------------------------------------------------------

/** Declined, her bank asks her to confirm it, or her bank returned it. Never Stripe's own code. */
export type PaymentProblem = 'declined' | 'needs_authentication' | 'returned';

export interface PaymentProblemEmail {
  readonly firstName: string | null;
  readonly amountCents: number;
  readonly invoiceNumber: string;
  readonly why: PaymentProblem;
  /**
   * An automatic charge ended, so her saved method is not tried again for this invoice. Never said
   * where her bank asks her to confirm the payment (`savedMethodSaid`).
   */
  readonly savedMethodStopped: boolean;
  /**
   * Her live link to the invoice, or null for the no-link form; where `atHome` is set, her invoice page
   * at home, or null for a person who holds no `client` door there, whose email takes its no-door form.
   */
  readonly link: string | null;
  /**
   * Whether the host sends her to home's pages (`CLIENT_PAGES_AT_HOME`; concern B1b): `SIGN_IN_LINE`
   * follows her link (D13), and with no link the sentence naming her invoice page leaves with `NO_LINK`
   * (D4). Absent or false, every word is as it was.
   */
  readonly atHome?: boolean;
}

/** What became of it, the whole of what she is told: "your bank declined it" and nothing of why. */
const WHAT_HAPPENED: Readonly<Record<PaymentProblem, string>> = Object.freeze({
  declined: 'your bank declined it. Nothing was charged.',
  needs_authentication: 'your bank asks you to confirm the payment yourself. Nothing was charged.',
  returned: 'your bank returned it, so nothing has been taken.',
});

/**
 * WHETHER E5 SAYS HER SAVED METHOD IS NOT TRIED AGAIN: wherever an automatic charge ended, declined or
 * returned, and never where her bank asks her to confirm the payment, since that same email asks her
 * to confirm the very payment the sentence would say is not tried again (decided by ops on 2026-10-19
 * within the owner's rules, posted on ops #76).
 */
const savedMethodSaid = (input: PaymentProblemEmail): boolean =>
  input.savedMethodStopped && input.why !== 'needs_authentication';

/**
 * E5, in its three forms. At home (`atHome`), her link is followed by the sign-in sentence; with no
 * link, for a person who holds no `client` door there, the sentence naming her invoice page leaves with
 * `NO_LINK`, and nothing is said in its place (`docs/plan-clients-only-home.md` D4).
 */
export function paymentProblemEmail(input: PaymentProblemEmail): EmailWords {
  const amount = formatCents(input.amountCents);
  const atHome = input.atHome === true;
  const link = input.link;
  const lead =
    input.why === 'needs_authentication'
      ? 'You can confirm it, or pay another way, from your invoice page'
      : 'You can pay another way from your invoice page';
  return {
    subject: `Your payment of ${amount} to Faithfully Kept did not go through`,
    text: emailText(input.firstName, [
      `Your payment of ${amount} for invoice ${input.invoiceNumber} did not go through: ${WHAT_HAPPENED[input.why]}${savedMethodSaid(input) ? ' We will not try your saved payment method again for this invoice.' : ''}`,
      link === null ? !atHome && `${lead}. ${NO_LINK}` : `${lead}:\n${link}`,
      link !== null && atHome && SIGN_IN_LINE,
      NEVER_CHANGE,
      QUESTIONS,
    ]),
    amountsCents: figures(input.amountCents),
  };
}

// ---------------------------------------------------------------------------------------------
// E6. Past due (`invoice_overdue`)
// ---------------------------------------------------------------------------------------------

/** An invoice past due: its number, what is still owed, and the clean it bills. */
export interface PastDueInvoice {
  readonly number: string;
  readonly owedCents: number;
  readonly visitDay: string | null;
}

export interface PastDueEmail extends PastDueInvoice {
  readonly firstName: string | null;
  readonly dueOn: string;
  /** Her other invoices past due in the same run, one sentence each (D12's one email a week). */
  readonly alsoPastDue: readonly PastDueInvoice[];
  /**
   * Her live link to the invoice, or null for the no-link form; where `atHome` is set, her invoice page
   * at home, or null for a person who holds no `client` door there, whose email takes its no-door form.
   */
  readonly link: string | null;
  /**
   * Whether the host sends her to home's pages (`CLIENT_PAGES_AT_HOME`; concern B1b): `SIGN_IN_LINE`
   * follows her link (D13), and with no link every sentence and clause naming an invoice page leaves
   * with `NO_LINK` (D4). Absent or false, every word is as it was.
   */
  readonly atHome?: boolean;
}

/** "Invoice FK-42, for your clean on Thursday, October 29,", or "Invoice FK-42" with no clean. */
const invoiceFor = (invoice: PastDueInvoice): string =>
  invoice.visitDay === null
    ? `Invoice ${invoice.number}`
    : `Invoice ${invoice.number}, for your clean on ${dayWords(invoice.visitDay)},`;

/**
 * E6. Once per invoice, the others past due named in it, so she hears of all of it in one email.
 *
 * At home (`atHome`), her link is followed by the sign-in sentence; with no link, for a person who
 * holds no `client` door there, she has no invoice page at home, so the sentence naming hers leaves
 * with `NO_LINK`, and so does the clause naming each other invoice's own page, the sentence ending where
 * it stood; nothing is said in their place (`docs/plan-clients-only-home.md` section 3, D4).
 */
export function pastDueEmail(input: PastDueEmail): EmailWords {
  const owed = formatCents(input.owedCents);
  const atHome = input.atHome === true;
  const link = input.link;
  // Whether she has an invoice page to be told of: always today, and at home only past her door.
  const pages = !(atHome && link === null);
  const also = input.alsoPastDue.map(
    (invoice) =>
      `${invoiceFor(invoice)} is also past due, with ${formatCents(invoice.owedCents)} still owed${pages ? '; every way to pay it is on its own invoice page' : ''}.`,
  );
  return {
    subject: `A reminder about invoice ${input.number} from Faithfully Kept: ${owed} still owed`,
    text: emailText(input.firstName, [
      `${invoiceFor(input)} was due on ${dayWords(input.dueOn)}, and ${owed} is still owed. If you have already paid, thank you, and please ignore this.`,
      also.length > 0 && also.join(' '),
      link === null
        ? !atHome && `Every way to pay is on your invoice page. ${NO_LINK}`
        : `Every way to pay is on your invoice page:\n${link}`,
      link !== null && atHome && SIGN_IN_LINE,
      NEVER_CHANGE,
      QUESTIONS,
    ]),
    amountsCents: figures(input.owedCents, ...input.alsoPastDue.map((one) => one.owedCents)),
  };
}

// ---------------------------------------------------------------------------------------------
// E7. The reminder before a visit, by email (`visit_reminder`)
// ---------------------------------------------------------------------------------------------

export interface VisitReminderEmail {
  readonly firstName: string | null;
  /** The visit's `scheduled_date`. */
  readonly day: string;
  /** `windowText(window_start, window_end)`, "9:00 am to 1:00 pm", or null for no window. */
  readonly window: string | null;
  /** The home's label, passed only where the household has more than one live home. */
  readonly homeLabel: string | null;
  /** A home watch visit, said as a home check. */
  readonly homeCheck: boolean;
  /** An ask from home not yet decided, to skip or to move this visit (D6), or null. */
  readonly openAsk: 'skip' | 'move' | null;
}

/** E7. The text's closing line in the email's form: `QUESTIONS`, no "text" (home's rule 8). */
export function visitReminderEmail(input: VisitReminderEmail): EmailWords {
  const day = dayWords(input.day);
  const label = typed(input.homeLabel);
  const where = label ? ` at the ${label}` : '';
  const window = typed(input.window);
  const when = window ? `, ${window}` : '';
  const reminder = input.homeCheck
    ? `A reminder that we will check your home${where} on ${day}${when}.`
    : `A reminder that your clean${where} is on ${day}${when}.`;
  const ask =
    input.openAsk === null
      ? ''
      : ` You have asked us to ${input.openAsk} this clean, and the office will confirm with you. Until then it stays on ${day}.`;
  return {
    subject: input.homeCheck
      ? `A reminder of your home check on ${day}`
      : `A reminder of your clean on ${day}`,
    text: emailText(input.firstName, [`${reminder}${ask} ${QUESTIONS}`]),
    amountsCents: [],
  };
}

// ---------------------------------------------------------------------------------------------
// E8. The fixed subjects of the emails the office writes itself
// ---------------------------------------------------------------------------------------------

/** Each kind the office writes by email, with what its subject names. */
export type OfficeEmailSubject =
  | { readonly kind: 'schedule_change'; readonly day: string }
  | { readonly kind: 'booking_confirmation' | 'visit_finished'; readonly day: string | null }
  | { readonly kind: 'access_problem' | 'reply' }
  | { readonly kind: 'invoice_sent'; readonly number: string | null };

/** The office's own words, as approved at its door, under the subject of its kind. */
export type OfficeEmail = OfficeEmailSubject & { readonly body: string };

/** E8: the fixed subject of each kind, the change's or the visit's day where it names one. */
export function officeEmailSubject(of: OfficeEmailSubject): string {
  switch (of.kind) {
    case 'schedule_change':
      return `A change to your clean on ${dayWords(of.day)}`;
    case 'booking_confirmation':
      return of.day === null
        ? 'Your clean with Faithfully Kept is booked'
        : `Your clean on ${dayWords(of.day)} is booked`;
    case 'visit_finished':
      return of.day === null ? 'Your clean is done' : `Your clean on ${dayWords(of.day)} is done`;
    case 'access_problem':
      return 'We could not get in for your clean today';
    case 'reply':
      return 'A reply from Faithfully Kept';
    case 'invoice_sent':
      return of.number === null
        ? 'Your invoice from Faithfully Kept'
        : `Your Faithfully Kept invoice ${of.number}`;
  }
}

/** E8: the office's words, the footer appended. Their claims check is the door's (`refuseClaims`). */
export function officeEmail(input: OfficeEmail): EmailWords {
  return {
    subject: officeEmailSubject(input),
    text: `${input.body.trim()}\n\n${FOOTER}`,
    amountsCents: [],
  };
}

// ---------------------------------------------------------------------------------------------
// The words as section 2 prints them, and their version
// ---------------------------------------------------------------------------------------------

/** One form of one email, filled with section 2's example, named as the test names it. */
export interface EmailSample {
  readonly name: string;
  readonly words: EmailWords;
}

/** Section 2's refund: Karen's $50.00 to her card on Thursday, October 29, for invoice FK-42. */
const SAMPLE_REFUND: RefundEmail = {
  firstName: 'Karen',
  amountCents: 5000,
  refundedOn: '2026-10-29',
  method: 'card',
  invoices: [{ number: 'FK-42', stillOwedCents: 0 }],
};

/**
 * EVERY FORM SECTION 2 PRINTS, filled with its example: Karen, invoice FK-42 of $212.00 for the clean
 * of Thursday, October 29 at the Palm Harbor house, due Thursday, November 5, receipt FK-R7, her link
 * `<link>`. What `EMAIL_WORDS_VERSION` is the hash of, beside the fixed lines.
 */
export function emailSamples(): EmailSample[] {
  const link = '<link>';
  const firstName = 'Karen';
  const invoice: InvoiceEmail = {
    firstName,
    number: 'FK-42',
    totalCents: 21200,
    dueOn: '2026-11-05',
    visitDay: '2026-10-29',
    homeLabel: 'Palm Harbor house',
    link,
    replaces: null,
    door: true,
    savedMethod: null,
  };
  // The business hours days first, as `businessHoursWords` writes them and the office's texts already
  // say them: decided by ops on 2026-10-19 within the owner's rules and recorded on ops #76, where
  // section 2's example line printed them time first.
  const visa = {
    words: 'Visa ending 4242',
    businessHours: 'Monday to Saturday, 9:00 am to 6:00 pm',
    receiptByEmail: true,
  };
  const reminder: SurveyReminderEmail = { firstName, visitDay: '2026-10-29', link };
  const zelle = {
    how: 'paid',
    method: 'zelle',
    invoices: [{ number: 'FK-42', stillOwedCents: 0 }],
    tipCents: 0,
    leftOverCents: 0,
  } as const;
  const receipt: ReceiptEmail = {
    firstName,
    receiptNumber: 'FK-R7',
    amountCents: 21200,
    receivedOn: '2026-10-28',
    link,
    door: true,
    paid: zelle,
  };
  const charged = {
    how: 'charged',
    savedMethod: 'Visa ending 4242',
    invoiceNumber: 'FK-42',
    afterOfficeCheck: false,
  } as const;
  const refund = SAMPLE_REFUND;
  const cancelled: CancelledInvoiceEmail = { firstName, number: 'FK-41', visitDay: '2026-10-29' };
  const problem: PaymentProblemEmail = {
    firstName,
    amountCents: 21200,
    invoiceNumber: 'FK-42',
    why: 'declined',
    savedMethodStopped: true,
    link,
  };
  const pastDue: PastDueEmail = {
    firstName,
    number: 'FK-42',
    owedCents: 21200,
    dueOn: '2026-11-05',
    visitDay: '2026-10-29',
    alsoPastDue: [],
    link,
  };
  const fk43 = { number: 'FK-43', owedCents: 18000, visitDay: '2026-11-05' };
  const visit: VisitReminderEmail = {
    firstName,
    day: '2026-10-29',
    window: '9:00 am to 1:00 pm',
    homeLabel: 'Palm Harbor house',
    homeCheck: false,
    openAsk: null,
  };
  const body = "<the office's words>";
  const samples: [string, EmailWords][] = [
    ['E1', invoiceEmail(invoice)],
    ['E1 one home', invoiceEmail({ ...invoice, homeLabel: null })],
    ['E1 no visit', invoiceEmail({ ...invoice, visitDay: null })],
    ['E1 a replacement', invoiceEmail({ ...invoice, replaces: 'FK-41' })],
    ['E1 no door', invoiceEmail({ ...invoice, door: false })],
    ['E1 her saved method', invoiceEmail({ ...invoice, savedMethod: visa })],
    [
      'E1 her saved method, no receipt by email',
      invoiceEmail({ ...invoice, savedMethod: { ...visa, receiptByEmail: false } }),
    ],
    // On a host whose clients reach home (`docs/plan-clients-only-home.md` concern B1a): her invoice
    // page there with the sign-in sentence after it, and the door line left out though her door is
    // granted (D1, D13); and the no-door form, for a payer who holds no `client` door there (D4).
    ['E1 at home', invoiceEmail({ ...invoice, atHome: true })],
    ['E1 at home, her saved method', invoiceEmail({ ...invoice, atHome: true, savedMethod: visa })],
    ['E1 at home, no door', invoiceEmail({ ...invoice, atHome: true, link: null, door: false })],
    [
      'E1 at home, no door, her saved method',
      invoiceEmail({ ...invoice, atHome: true, link: null, door: false, savedMethod: visa }),
    ],
    ['E1r', surveyReminderEmail(reminder)],
    ['E1r no clean named', surveyReminderEmail({ ...reminder, visitDay: null })],
    ['E1r at home', surveyReminderEmail({ ...reminder, atHome: true })],
    ['E2', receiptEmail(receipt)],
    ['E2 by card', receiptEmail({ ...receipt, paid: { ...zelle, method: 'card' } })],
    [
      'E2 from her bank account',
      receiptEmail({ ...receipt, paid: { ...zelle, method: 'bank_transfer' } }),
    ],
    ['E2 by check', receiptEmail({ ...receipt, paid: { ...zelle, method: 'check' } })],
    ['E2 in cash', receiptEmail({ ...receipt, paid: { ...zelle, method: 'cash' } })],
    ['E2 another way', receiptEmail({ ...receipt, paid: { ...zelle, method: 'other' } })],
    [
      'E2 part paid',
      receiptEmail({
        ...receipt,
        amountCents: 10000,
        paid: { ...zelle, invoices: [{ number: 'FK-42', stillOwedCents: 11200 }] },
      }),
    ],
    [
      'E2 several invoices',
      receiptEmail({
        ...receipt,
        amountCents: 37400,
        paid: {
          ...zelle,
          invoices: [
            { number: 'FK-41', stillOwedCents: 0 },
            { number: 'FK-42', stillOwedCents: 5000 },
          ],
        },
      }),
    ],
    [
      'E2 a tip',
      receiptEmail({ ...receipt, amountCents: 23200, paid: { ...zelle, tipCents: 2000 } }),
    ],
    [
      'E2 money left over',
      receiptEmail({ ...receipt, amountCents: 23200, paid: { ...zelle, leftOverCents: 2000 } }),
    ],
    ['E2 no live link', receiptEmail({ ...receipt, link: null })],
    ['E2 no door', receiptEmail({ ...receipt, door: false })],
    ['E2 automatic', receiptEmail({ ...receipt, paid: charged })],
    [
      'E2 automatic, released by the office',
      receiptEmail({ ...receipt, paid: { ...charged, afterOfficeCheck: true } }),
    ],
    [
      'E2 automatic, her bank account',
      receiptEmail({ ...receipt, paid: { ...charged, savedMethod: 'Chase account ending 6789' } }),
    ],
    // On a host whose clients reach home (`docs/plan-clients-only-home.md` concern B1b): her invoice
    // page there with the sign-in sentence after it, the door line left out though her door is granted
    // (D1, D13); and the no-door form, for a person who holds no `client` door there (D4).
    ['E2 at home', receiptEmail({ ...receipt, atHome: true })],
    ['E2 at home, no door', receiptEmail({ ...receipt, atHome: true, link: null, door: false })],
    ['E3', refundEmail(refund)],
    ['E3 to her bank account', refundEmail({ ...refund, method: 'bank_transfer' })],
    ['E3 by Zelle', refundEmail({ ...refund, method: 'zelle' })],
    ['E3 by check', refundEmail({ ...refund, method: 'check' })],
    ['E3 in cash', refundEmail({ ...refund, method: 'cash' })],
    ['E3 another way', refundEmail({ ...refund, method: 'other' })],
    [
      'E3 still owed',
      refundEmail({ ...refund, invoices: [{ number: 'FK-42', stillOwedCents: 6200 }] }),
    ],
    ['E3 no invoice', refundEmail({ ...refund, invoices: [] })],
    // Section 2 does not print it; approved by ops on 2026-10-19 within the owner's rules (ops #76).
    [
      'E3 several invoices',
      refundEmail({
        ...refund,
        invoices: [
          { number: 'FK-41', stillOwedCents: 0 },
          { number: 'FK-42', stillOwedCents: 6200 },
        ],
      }),
    ],
    ['E4', cancelledInvoiceEmail(cancelled)],
    ['E4 no visit', cancelledInvoiceEmail({ ...cancelled, visitDay: null })],
    ['E5', paymentProblemEmail(problem)],
    ['E5 her bank asks her', paymentProblemEmail({ ...problem, why: 'needs_authentication' })],
    [
      'E5 returned',
      paymentProblemEmail({ ...problem, why: 'returned', savedMethodStopped: false }),
    ],
    ['E5 returned, an automatic charge', paymentProblemEmail({ ...problem, why: 'returned' })],
    ['E5 no live link', paymentProblemEmail({ ...problem, link: null })],
    [
      'E5 her bank asks her, no live link',
      paymentProblemEmail({ ...problem, why: 'needs_authentication', link: null }),
    ],
    // At home (concern B1b): her invoice page there with the sign-in sentence after it, and the
    // no-door form, which leaves out the sentence naming her invoice page and `NO_LINK` (D4, D13).
    ['E5 at home', paymentProblemEmail({ ...problem, atHome: true })],
    [
      'E5 her bank asks her, at home',
      paymentProblemEmail({ ...problem, why: 'needs_authentication', atHome: true }),
    ],
    ['E5 at home, no door', paymentProblemEmail({ ...problem, atHome: true, link: null })],
    [
      'E5 her bank asks her, at home, no door',
      paymentProblemEmail({ ...problem, why: 'needs_authentication', atHome: true, link: null }),
    ],
    ['E6', pastDueEmail(pastDue)],
    ['E6 no visit', pastDueEmail({ ...pastDue, visitDay: null })],
    ['E6 no live link', pastDueEmail({ ...pastDue, link: null })],
    ['E6 also past due', pastDueEmail({ ...pastDue, alsoPastDue: [fk43] })],
    [
      'E6 also past due, no visit',
      pastDueEmail({ ...pastDue, alsoPastDue: [{ ...fk43, visitDay: null }] }),
    ],
    // At home (concern B1b): her invoice page there with the sign-in sentence after it; and the
    // no-door form, which leaves out the sentence naming her invoice page, `NO_LINK`, and the clause
    // naming each other invoice's own page (D4, D13).
    ['E6 at home', pastDueEmail({ ...pastDue, atHome: true })],
    ['E6 at home, no door', pastDueEmail({ ...pastDue, atHome: true, link: null })],
    [
      'E6 also past due, at home, no door',
      pastDueEmail({ ...pastDue, alsoPastDue: [fk43], atHome: true, link: null }),
    ],
    ['E7', visitReminderEmail(visit)],
    ['E7 one home', visitReminderEmail({ ...visit, homeLabel: null })],
    ['E7 no window', visitReminderEmail({ ...visit, window: null })],
    ['E7 home check', visitReminderEmail({ ...visit, homeCheck: true })],
    ['E7 home check, one home', visitReminderEmail({ ...visit, homeCheck: true, homeLabel: null })],
    ['E7 an ask to skip', visitReminderEmail({ ...visit, openAsk: 'skip' })],
    ['E7 an ask to move', visitReminderEmail({ ...visit, openAsk: 'move' })],
    ['E8 schedule_change', officeEmail({ kind: 'schedule_change', day: '2026-10-29', body })],
    [
      'E8 booking_confirmation',
      officeEmail({ kind: 'booking_confirmation', day: '2026-10-29', body }),
    ],
    [
      'E8 booking_confirmation, no visit',
      officeEmail({ kind: 'booking_confirmation', day: null, body }),
    ],
    ['E8 visit_finished', officeEmail({ kind: 'visit_finished', day: '2026-10-29', body })],
    ['E8 visit_finished, no visit', officeEmail({ kind: 'visit_finished', day: null, body })],
    ['E8 access_problem', officeEmail({ kind: 'access_problem', body })],
    ['E8 reply', officeEmail({ kind: 'reply', body })],
    ['E8 invoice_sent', officeEmail({ kind: 'invoice_sent', number: 'FK-42', body })],
    [
      'E8 invoice_sent, before numbering',
      officeEmail({ kind: 'invoice_sent', number: null, body }),
    ],
  ];
  return samples.map(([name, words]) => ({ name, words }));
}

/**
 * WHAT `EMAIL_WORDS_VERSION` IS THE HASH OF: the fixed lines, then every form of `emailSamples` as its
 * name, its subject and its text, as one JSON string.
 */
export function emailWordsCorpus(): string {
  return JSON.stringify({
    lines: [
      QUESTIONS,
      NEVER_CHANGE,
      HOW_TO_STOP,
      DOOR_LINE,
      FOOTER,
      NO_LINK,
      SIGN_IN_LINE,
      ANSWER_BY_REPLY,
    ],
    samples: emailSamples().map(({ name, words }) => [name, words.subject, words.text]),
  });
}
